import React, { useEffect, useState, useMemo, useContext } from "react";
import {
  Container,
  Spinner,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import styles from "../css/ambulatoryVisit.module.scss";
import CommonSearch from "Components/Common/CommonSearch";
import CommonTableIndexWise from "Components/Common/CommonTableIndexWise";
import PageTitle from "Components/Common/PageTitle";
import CommonButton from "Components/Common/CommonButton";
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from "common/constants/PermissionConstants";
import { useAuditService } from "modules/Audit/api/useAuditQuery";
import { showToastMessage } from "common/service/CommonService";
import FilePreviewModal from "modules/Study/Attachments/components/FilePreviewModal";
import StatCard from "Components/Common/StatCard";
import { AMBULATORY_VISIT_PATHS, STUDY_PATHS } from "Routes/paths";
import { BreadcrumbContext } from "Layouts/Header";
import { useStudyDetailsService } from "hooks/useCommonQuery";
import { useAmbulatoryVisitService } from "../api/useAmbulatoryVisitQuery";
import AssignSubjectsModal from "../components/AssignSubjectsModal";
import { visitcolumns } from "../helper/ambulatoryvisitconstants";

const AmbulatoryVisitList: React.FC = () => {
  document.title = "Ambulatory Visits | ZENTIXS VISTA";
  const navigate = useNavigate();
  const breadcrumbContext = useContext(BreadcrumbContext);

  useEffect(() => {
    breadcrumbContext?.setBreadcrumbItems?.([
      { text: "Study", link: STUDY_PATHS.LIST },
      { text: "Ambulatory Visits" },
    ]);

    return () => breadcrumbContext?.setBreadcrumbItems?.([]);
  }, []);

  const [visits, setVisits] = useState<any[]>([]);
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [activePeriodTab, setActivePeriodTab] = useState<string>("01");

  // Audit
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const { SelectAllMutation: AuditMutation } = useAuditService();

  const [assignOpen, setAssignOpen] = useState(false);
  const [visitToAssign, setVisitToAssign] = useState<any>(null);

  const studyid = Number(sessionStorage.getItem("activestudyid") || 1);

  // Fetch basic study details to get period numbers
  const { SelectQuery: StudyDetailsQuery } = useStudyDetailsService(
    null,
    null,
    studyid ?? undefined
  );
  const studyData = StudyDetailsQuery?.data?.data?.[0];

  const dynamicPeriods = useMemo(() => {
    const periodsList = studyData?.periods || [];
    return periodsList.map((p: any, idx: number) => {
      const pNo = p.period_no || idx + 1;
      const periodNum = String(pNo).padStart(2, "0");
      return {
        key: periodNum,
        label: `Period ${periodNum}`,
      };
    });
  }, [studyData?.periods]);

  useEffect(() => {
    if (dynamicPeriods.length > 0) {
      const exists = dynamicPeriods.some((p: any) => p.key === activePeriodTab);
      if (!exists) {
        setActivePeriodTab(dynamicPeriods[0].key);
      }
    }
  }, [dynamicPeriods, activePeriodTab]);

  const { SelectAllAmbulatoryVisit } = useAmbulatoryVisitService({
    studyid: studyid,
    searchtext: searchText,
    currentPage: currentPage,
    pageSize: 10,
  });

  // Sync state from react-query on load
  useEffect(() => {
    if (SelectAllAmbulatoryVisit.data) {
      setVisits((SelectAllAmbulatoryVisit.data as any)?.data || []);
    }
  }, [SelectAllAmbulatoryVisit.data]);

  const filteredVisits = useMemo(() => {
    if (dynamicPeriods.length === 0) return visits;
    return visits.filter((v: any) => {
      const pStr = String(v.period_number || v.period_no || "01").padStart(2, "0");
      return pStr === activePeriodTab;
    });
  }, [visits, activePeriodTab, dynamicPeriods]);

  // Compute stat card counters
  const stats = useMemo(() => {
    return {
      total: visits[0]?.totalcount || 0,
      finalized: visits[0]?.totalactivecount || 0,
      inProgress: visits[0]?.totalstartedcount || 0,
      completed: visits[0]?.totalcompletedcount || 0
    };
  }, [visits]);

  const onSearch = (value: string | null) => {
    setSearchText(value ?? "");
    setCurrentPage(1);
  };

  const onAuditClick = () => {
    const payload = {
      modulename: "Ambulatory Visit",
      studyid: studyid
    };

    AuditMutation.mutate(payload, {
      onSuccess: (data: any) => {
        if (data instanceof Blob) {
          const pdfFile = new File(
            [data],
            "Subject_Checkin_Checkout_Audit_Log.pdf",
            { type: "application/pdf" },
          );
          setPreviewFile(pdfFile);
          setPreviewOpen(true);
        }
      },
      onError: (error: any) => {
        showToastMessage("error", "Error fetching audit logs");
      },
    });
  };

  const handleViewVisit = (visit: any) => {
    navigate(AMBULATORY_VISIT_PATHS.VIEW.replace(":id", visit.visitid));
  };

  const getTableActions = (row: any) => {
    const isFinalized = row.visitstatus === 'active';
    if (isFinalized) {
      return {
        onView: (r: any) => handleViewVisit(r),
        onAssignSubjects: (r: any) => handleAssignSubjectsClick(r),
      };
    }
    else {
      return {
        onView: (r: any) => handleViewVisit(r),
      };
    }
  };

  const handleAssignSubjectsClick = (visit: any) => {
    setVisitToAssign(visit);
    setAssignOpen(true);
  };

  const handledetail = (row: any) => {
    navigate(AMBULATORY_VISIT_PATHS.DETAIL.replace(":visitId", row.visitid));
  };

  return (
    <div className={styles.dashboardContainer}>
      <Container fluid>
        {/* Header */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-2">
            <div>
              <PageTitle pageTitle="Ambulatory Visits" />
              <p className={styles.pageHeaderSubtitle}>
                Manage and monitor all scheduled ambulatory visits for this study.
              </p>
            </div>
            <div className="d-flex gap-2 align-items-center">
              <CommonButton
                module={PERMISSION_MODULES.AMBULATORY_VISIT}
                action={PERMISSION_ACTIONS.AUDIT}
                color="primary"
                size="sm"
                onClick={onAuditClick}
                disabled={AuditMutation.isPending}
                outline
                className="fw-600"
                style={{ height: "35px" }}
              >
                {AuditMutation.isPending ? (
                  <Spinner size="sm" className="me-2" />
                ) : (
                  <i className="ri-history-line me-1"></i>
                )}
                Audit
              </CommonButton>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="row g-3 mb-4">
          <div className="col">
            <StatCard
              title="Total Visits"
              value={stats.total}
              subtext="All configured visits"
              icon={<i className="ri-calendar-event-line" style={{ fontSize: "1.2rem" }} />}
              bgColor="#e0f2fe"
              iconColor="#0284c7"
              subtextIcon="ri-calendar-event-line"
              loading={SelectAllAmbulatoryVisit.isLoading}
            />
          </div>
          <div className="col">
            <StatCard
              title="Active Visits"
              value={stats.finalized}
              subtext="Ready for subject assignment"
              icon={<i className="ri-verified-badge-line" style={{ fontSize: "1.2rem" }} />}
              bgColor="#e0e7ff"
              iconColor="#4f46e5"
              subtextIcon="ri-checkbox-circle-fill"
              loading={SelectAllAmbulatoryVisit.isLoading}
            />
          </div>
          <div className="col">
            <StatCard
              title="Started Visits"
              value={stats.inProgress}
              subtext="Visits already started"
              icon={<i className="ri-pulse-line" style={{ fontSize: "1.2rem" }} />}
              bgColor="#fef3c7"
              iconColor="#d97706"
              subtextIcon="ri-loader-4-line"
              loading={SelectAllAmbulatoryVisit.isLoading}
            />
          </div>
          <div className="col">
            <StatCard
              title="Completed Visits"
              value={stats.completed}
              subtext="Visits successfully completed"
              icon={<i className="ri-checkbox-circle-line" style={{ fontSize: "1.2rem" }} />}
              bgColor="#dcfce7"
              iconColor="#16a34a"
              subtextIcon="ri-checkbox-circle-line"
              loading={SelectAllAmbulatoryVisit.isLoading}
            />
          </div>
        </div>

        {/* Table Panel */}
        <div className={styles.listPanel}>
        <div className="pb-3">
          <div className={styles.panelHeader}>
            <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
              <i className="ri-walk-line text-primary fs-18" />
              Ambulatory Visits List
            </h5>
            <CommonSearch onSearch={onSearch} searchText={searchText} placeholder="Search..." />
          </div>

          {/* Period select tabs */}
          {dynamicPeriods.length > 0 && (
            <div
              className="d-flex align-items-center gap-4 px-4 pt-3 mb-2"
              style={{ borderBottom: "2px solid #eff2f7" }}
            >
              {dynamicPeriods.map((p: any) => (
                <div
                  key={p.key}
                  className="position-relative cursor-pointer pb-2"
                  style={{
                    transition: "all 0.2s ease",
                    borderBottom:
                      activePeriodTab === p.key
                        ? "2px solid #1e6b91"
                        : "2px solid transparent",
                    marginBottom: "-2px",
                  }}
                  onClick={() => {
                    setActivePeriodTab(p.key);
                    setCurrentPage(1);
                  }}
                >
                  <span
                    className={`fw-semibold ${
                      activePeriodTab === p.key ? "text-primary" : "text-muted"
                    }`}
                    style={{ fontSize: "13px" }}
                  >
                    {p.label}
                  </span>
                </div>
              ))}
            </div>
          )}

          <div className="px-4 pt-3">
            <CommonTableIndexWise
              moduleName={PERMISSION_MODULES.AMBULATORY_VISIT}
              columns={visitcolumns(handledetail)}
              data={filteredVisits}
              actions={getTableActions}
              loading={SelectAllAmbulatoryVisit.isLoading || SelectAllAmbulatoryVisit.isPending || SelectAllAmbulatoryVisit.isFetching}
              pageSize={10}
              currentPage={currentPage}
              totalRecords={filteredVisits.length}
              onPageChange={(p) => setCurrentPage(p)}
            />
          </div>
        </div>
        </div>
      </Container>

      <AssignSubjectsModal
        isOpen={assignOpen}
        onClose={() => {
          setAssignOpen(false);
          setVisitToAssign(null);
        }}
        visit={visitToAssign}
      />

      <FilePreviewModal
        isOpen={previewOpen}
        file={previewFile}
        size="xl"
        onClose={() => {
          setPreviewOpen(false);
          setPreviewFile(null);
        }}
      />
    </div>
  );
};

export default AmbulatoryVisitList;
